import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import {
  CheckCircle2,
  Calendar,
  Clock,
  Users,
  Car,
  ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

/**
 * @param {{
 *   confirmation: {
 *     vehicle: {
 *       image: string;
 *       provider: string;
 *       vehicle_type: string;
 *       pickup_location: string;
 *       dropoff_location: string;
 *     };
 *     date: Date;
 *     returnDate?: Date | null;
 *     passengers: number;
 *     pickupTime: string;
 *     returnPickupTime?: string | null;
 *     isRoundTrip: boolean;
 *     totalAmount: number;
 *   };
 *   onBackToSearch: () => void;
 *   onProceedToAccommodation: () => void;
 *   onCompleteBooking: () => void;
 *   showNextStep: boolean;
 * }} props
 */
export default function TransportConfirmationView({
  confirmation,
  onBackToSearch,
  onProceedToAccommodation,
  onCompleteBooking,
  showNextStep,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);

  const {
    vehicle,
    date,
    returnDate,
    passengers,
    pickupTime,
    returnPickupTime,
    isRoundTrip,
    totalAmount,
  } = confirmation;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        <Card className="p-8 sm:p-12 text-center shadow-2xl bg-white">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-[#0B2421]"
          >
            <CheckCircle2 className="h-12 w-12 text-[#D4AF37]" />
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0B2421] mb-3">
              Transportation Selection Confirmed!
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Your transportation has been reserved
            </p>
          </motion.div>

          {/* Vehicle Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-left space-y-4 mb-8"
          >
            <div className="flex items-start gap-3">
              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                <img
                  src={vehicle.image}
                  alt={vehicle.provider}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="flex items-center gap-1 text-sm text-[#D4AF37] mb-2">
                  <Car className="h-4 w-4" />
                  <span>{vehicle.provider}</span>
                </div>
                <div className="text-xs text-gray-600 mb-1">{vehicle.vehicle_type}</div>
                <div className="flex items-center gap-2 font-semibold text-[#0B2421]">
                  <span className="text-sm">{vehicle.pickup_location}</span>
                  <ArrowRight className="h-4 w-4 text-[#D4AF37]" />
                  <span className="text-sm">{vehicle.dropoff_location}</span>
                </div>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Date</div>
                  <div className="font-semibold text-[#0B2421]">
                    {format(date, "dd MMM yyyy")}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Pickup Time</div>
                  <div className="font-semibold text-[#0B2421]">{pickupTime}</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Passengers</div>
                  <div className="font-semibold text-[#0B2421]">{passengers}</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="text-xs font-bold text-[#D4AF37]">SGD</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Subtotal</div>
                  <div className="font-semibold text-[#0B2421]">SGD {totalAmount.toFixed(2)}</div>
                </div>
              </div>
            </div>

            {isRoundTrip && returnDate && (
              <div className="pt-3 border-t border-gray-100 text-sm text-gray-600">
                <span className="font-medium text-[#D4AF37]">Return: </span>
                {format(returnDate, "dd MMM yyyy")}
                {returnPickupTime ? ` at ${returnPickupTime}` : ""}
              </div>
            )}
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="space-y-3"
          >
            <Button
              onClick={onCompleteBooking}
              className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg"
            >
              Complete Booking Now
            </Button>
            {showNextStep && (
              <Button
                onClick={onProceedToAccommodation}
                className="w-full h-14 font-semibold text-lg bg-gray-100 text-[#0B2421]"
              >
                Continue to Accommodation
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={onBackToSearch}
              className="w-full h-10 text-gray-600 hover:bg-transparent hover:underline hover:text-[#0B2421]/70"
            >
              Change Transport Selection
            </Button>
          </motion.div>

          {/* Info Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-6 p-4 border border-amber-200 bg-amber-50 rounded-xl"
          >
            <p className="text-sm text-amber-700">
              💡 Complete now or add accommodation for your trip
            </p>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  );
}
